import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart3, Edit2, LayoutDashboard, ListChecks, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { weeklyReportApi as api, apiErrorMessage } from '../../services/weeklyReportApi';
import { absent, buildReport, formPayload, monday, money, normalize, today } from './weeklyReport';
import './serviciosProfesionales.css';
import './ReporteSemanalPage.css';

const errorText = (error) => apiErrorMessage(error, 'No se pudo completar la operación.');
const dateLabel = (value) => value ? value.split('-').reverse().join('/') : 'Sin dato';
const PAGE_SIZE = 25;

function WeeklyDialog({ title, busy, onClose, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    ref.current.querySelector('input, select, textarea, button')?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);
  const keyboard = (event) => {
    if (event.key === 'Escape' && !busy) onClose();
    if (event.key !== 'Tab') return;
    const controls = [...ref.current.querySelectorAll('input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex="0"]')];
    if (!controls.length) { event.preventDefault(); ref.current.focus(); return; }
    if (event.shiftKey && (document.activeElement === controls[0] || !controls.includes(document.activeElement))) {
      event.preventDefault(); controls[controls.length - 1].focus();
    } else if (!event.shiftKey && (document.activeElement === controls[controls.length - 1] || !controls.includes(document.activeElement))) {
      event.preventDefault(); controls[0].focus();
    }
  };
  return <div className="sp-overlay"><section ref={ref} className="sp-dialog weekly-dialog" role="dialog"
    aria-modal="true" aria-label={title} tabIndex={-1} onKeyDown={keyboard}>
    <header className="sp-section-heading"><h2>{title}</h2><button className="sp-icon-button" aria-label="Cerrar"
      disabled={busy} onClick={onClose}><X size={17} /></button></header>{children}
  </section></div>;
}

function Cell({ value, field, onRead }) {
  if (absent(value)) return <span className="weekly-muted">Sin dato</span>;
  if (field.type === 'textarea') return <button className="weekly-read-text" onClick={onRead}>
    {field.key.startsWith('comentarios') ? 'Ver comentarios' : 'Ver detalle'}
  </button>;
  if (field.type === 'percent') return <span className="weekly-progress">
    <progress max="1" value={Number(value)} aria-label={field.label} />{Number((Number(value) * 100).toFixed(2))}%
  </span>;
  if (field.key === 'semaforo') return <span className={`weekly-status weekly-${normalize(value)}`}>{value}</span>;
  if (field.type === 'date') return dateLabel(value);
  if (field.type === 'money') return money(value);
  return String(value);
}

function ReportTable({ table, records, onEdit, onDelete }) {
  const [detail, setDetail] = useState(null);
  return <><div className="sp-table-wrap"><table aria-label={table.label}>
    <thead><tr>{onEdit && <th>Acciones</th>}{table.fields.map((field) => <th key={field.key} scope="col">{field.label}</th>)}</tr></thead>
    <tbody>{!records.length && <tr><td className="sp-empty" colSpan={table.fields.length + (onEdit ? 1 : 0)}>Sin registros para este alcance.</td></tr>}
      {records.map((row) => <tr key={row.id}>
        {onEdit && <td><div className="sp-actions weekly-row-actions">
          <button className="sp-icon-button sp-tone-accent" aria-label={`Editar registro ${row.id}`} onClick={() => onEdit(row)}><Edit2 size={15} /></button>
          <button className="sp-icon-button sp-tone-red" aria-label={`Eliminar registro ${row.id}`} onClick={() => onDelete(row)}><Trash2 size={15} /></button>
        </div></td>}
        {table.fields.map((field) => <td key={field.key}><Cell value={row[field.key]} field={field}
          onRead={() => setDetail({ title: field.label, text: String(row[field.key]), context: row.cliente || table.label })} /></td>)}
      </tr>)}</tbody>
  </table></div>
    {detail && <WeeklyDialog title={detail.title} onClose={() => setDetail(null)}>
      <p>{detail.context}</p>
      <div className="weekly-text-detail" tabIndex={0} role="region" aria-label={`Contenido de ${detail.title}`}>{detail.text}</div>
      <footer className="sp-actions sp-editor-footer"><button onClick={() => setDetail(null)}>Entendido</button></footer>
    </WeeklyDialog>}
  </>;
}

function StatusSummary({ statuses, label }) {
  return <div aria-label={label} className="weekly-status-summary">
    {Object.entries(statuses).map(([name, count]) => <span className={`weekly-status weekly-${name.replace(' ', '-')}`} key={name}>{name}: {count}</span>)}
  </div>;
}

function Dashboard({ data, tables }) {
  const report = useMemo(() => buildReport(data), [data]);
  return <>
    <div className="sp-kpis">
      <article><span>Clientes SA en verde</span><strong>{report.adminStatuses.verde} / {report.clientCount}</strong>
        <small>{report.sla} tickets fuera de SLA · {report.incidents} incidentes mayores · {report.slaMissing} filas con conteos incompletos</small></article>
      <article><span>Registros de proyectos SP</span><strong>{report.projects.length}</strong><small>{report.escalations} requieren escalamiento</small></article>
      <article><span>Pipeline abierto · USD/mes</span><strong>{money(report.pipelineAmount)}</strong>
        <small>{report.pipeline.length} registros · {report.pipelineMissing} sin monto</small></article>
      <article><span>Temas para dirección</span><strong>{report.issues.length}</strong>
        <small>{report.issues.filter((row) => normalize(row.impacto) === 'alto').length} de impacto alto</small></article>
    </div>
    <div className="weekly-dashboard-grid">
      <section className="weekly-panel"><h2>Semáforo SA</h2><StatusSummary statuses={report.adminStatuses} label="Semáforo SA" /></section>
      <section className="weekly-panel"><h2>Semáforo SP</h2><StatusSummary statuses={report.projectStatuses} label="Semáforo SP" />
        <p>Presupuesto informado: {money(report.budget)} USD · {report.budgetMissing} registros sin presupuesto.</p></section>
      <section className="weekly-panel"><h2>Pipeline por práctica</h2><p>Excluye Ganada y Perdida.</p>
        {report.pipelineGroups.map(({ name, amount }) => <div className="weekly-bar" key={name}><span>{name}</span>
          <progress max={Math.max(report.pipelineAmount, 1)} value={amount} aria-label={name} /><strong>{money(amount)}</strong></div>)}
        {!report.pipelineGroups.length && <p>Sin oportunidades abiertas.</p>}
      </section>
      <section className="weekly-panel"><h2>Registros por semana</h2><p>Semana agrupada por lunes; no representa proyectos únicos.</p>
        {report.weeks.map(([week, count]) => <div className="weekly-bar" key={week}><span>{dateLabel(week)}</span>
          <progress max={Math.max(...report.weeks.map((entry) => entry[1]), 1)} value={count} aria-label={`Semana ${week}`} /><strong>{count}</strong></div>)}
        {!report.weeks.length && <p>Sin registros en el rango.</p>}
      </section>
    </div>
    <section className="weekly-panel"><h2>Tickets del mes</h2>
      <p>Última captura global del mes de la fecha «Hasta», hasta ese día. No se suma entre cortes ni se filtra por cliente, nube o responsable.</p>
      {report.ticket ? <>
        <p>{report.ticket.periodo} · captura al {dateLabel(report.ticket.fecha_corte)}</p>
        <div className="sp-kpis">
          {[['Total tickets', report.ticket.total_tickets], ['Abiertos', report.ticket.tickets_abiertos],
            ['Cerrados', report.ticket.tickets_cerrados], ['SLA OK', absent(report.ticket.sla_ok) ? null : `${Number((Number(report.ticket.sla_ok) * 100).toFixed(2))}%`]]
            .map(([label, value]) => <article key={label}><span>{label}</span><strong>{absent(value) ? 'Sin dato' : value}</strong></article>)}
        </div>
        <p>Fuente: {report.ticket.fuente || 'Sin dato'}</p>
      </> : <p>No hay captura de tickets para ese mes y fecha.</p>}
    </section>
    {[['proyectos_sp', report.projects], ['servicios_admin', report.services], ['pipeline', data.pipeline || []],
      ['highlights_riesgos', report.issues]].map(([kind, records]) => (
      <details className="weekly-panel" key={kind}><summary>{tables[kind].label} · {records.length} registros</summary>
        {kind === 'highlights_riesgos' && <p>Marcados para dirección y no cerrados. Todos los temas están disponibles en Gestión.</p>}
        <ReportTable table={tables[kind]} records={records} />
      </details>
    ))}
  </>;
}

function RecordForm({ table, record, options, onClose, onSave }) {
  const [form, setForm] = useState(() => Object.fromEntries(table.fields.map((field) => [field.key,
    record ? (field.type === 'percent' && !absent(record[field.key]) ? Number(record[field.key]) * 100 : record[field.key] ?? '')
      : field.type === 'date' && ['fecha', 'fecha_corte'].includes(field.key) ? today() : '',
  ])));
  const [saving, setSaving] = useState(false), [error, setError] = useState('');
  const title = `${record ? 'Editar' : 'Nuevo registro'} · ${table.label}`;
  const submit = async (event) => {
    event.preventDefault(); setSaving(true); setError('');
    try { await onSave(formPayload(table.fields, form)); }
    catch (failure) { setError(errorText(failure)); }
    finally { setSaving(false); }
  };
  return <WeeklyDialog title={title} busy={saving} onClose={onClose}>
    <p>Los campos sin información se conservan vacíos. Los porcentajes se capturan de 0 a 100.</p>
    {table.label === 'Catalogos' && <p>Cada columna es una lista independiente. Los valores de una misma fila no relacionan a una persona con una nube.</p>}
    {table.label === 'Diccionario' && <p>Documentación de los campos. Editarla no cambia las columnas físicas ni las reglas de validación.</p>}
    <form onSubmit={submit}><fieldset disabled={saving} className="sp-editor-fields">
      {table.fields.map((field) => <label key={field.key} htmlFor={`weekly-${field.key}`}>{field.label}{field.required ? ' *' : ''}
        {field.type === 'textarea' ? <textarea id={`weekly-${field.key}`} aria-label={`${field.label}${field.required ? ' *' : ''}`}
          required={field.required} maxLength={10000} rows={4}
          value={form[field.key]} onChange={(event) => setForm({ ...form, [field.key]: event.target.value })} />
          : <input id={`weekly-${field.key}`} required={field.required} aria-label={`${field.label}${field.required ? ' *' : ''}`}
            aria-describedby={field.catalog ? `weekly-help-${field.key}` : undefined}
            type={field.type === 'date' ? 'date' : ['integer', 'money', 'percent'].includes(field.type) ? 'number' : 'text'}
            min={['integer', 'money', 'percent'].includes(field.type) ? 0 : undefined}
            max={field.type === 'percent' ? 100 : undefined} step={field.type === 'integer' ? 1 : 'any'}
            maxLength={300} list={field.catalog ? `weekly-options-${field.key}` : undefined}
            value={form[field.key]} onChange={(event) => setForm({ ...form, [field.key]: event.target.value })} />}
        {field.catalog && <datalist id={`weekly-options-${field.key}`}>{(options[field.catalog] || []).map((value) => <option key={value} value={value} />)}</datalist>}
        {field.catalog && <small id={`weekly-help-${field.key}`}>Sugerencias de Catalogos; puedes conservar o escribir otro valor.</small>}
      </label>)}
    </fieldset>
      {error && <p role="alert" className="sp-feedback error">{error} Tus cambios siguen en el formulario.</p>}
      <footer className="sp-actions sp-editor-footer"><button type="button" disabled={saving} onClick={onClose}>Cancelar</button>
        <button type="submit" className="sp-primary" disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button></footer>
    </form>
  </WeeklyDialog>;
}

export default function ReporteSemanalPage() {
  const [tables, setTables] = useState(null), [options, setOptions] = useState({});
  const [view, setView] = useState('dashboard'), [kind, setKind] = useState('proyectos_sp');
  const [range, setRange] = useState(null), [filters, setFilters] = useState({ cliente: '', nube: '', responsable: '' });
  const [applied, setApplied] = useState(null);
  const [data, setData] = useState(null), [list, setList] = useState(null);
  const [undated, setUndated] = useState([]);
  const [search, setSearch] = useState(''), [query, setQuery] = useState(''), [offset, setOffset] = useState(0);
  const [version, setVersion] = useState(0), [bootVersion, setBootVersion] = useState(0);
  const [loading, setLoading] = useState(true), [error, setError] = useState(''), [notice, setNotice] = useState('');
  const [modal, setModal] = useState(null), [deleting, setDeleting] = useState(null), [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    Promise.all([api.schema(), api.options()]).then(([schema, catalogs]) => {
      if (!active) return;
      setTables(schema.tables); setOptions(catalogs.options);
      const end = catalogs.latest_date || today(), initial = { start: monday(end), end };
      setRange((current) => current || initial);
      setApplied((current) => current || { ...initial, cliente: '', nube: '', responsable: '' });
    }).catch((failure) => { if (active) { setTables(null); setLoading(false); setError(errorText(failure)); } });
    return () => { active = false; };
  }, [bootVersion]);
  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search.trim()); setOffset(0); }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    if (!tables || !applied) return undefined;
    let active = true;
    setLoading(true); setError('');
    if (view === 'dashboard') setData(null);
    else setList(null);
    const request = view === 'dashboard' ? api.dashboard(applied) : api.records(kind, { search: query, limit: PAGE_SIZE, offset });
    request.then((result) => {
      if (!active) return;
      if (view === 'dashboard') { setData(result.data); setUndated(result.undated || []); }
      else setList(result);
    }).catch((failure) => { if (active) setError(errorText(failure)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [tables, applied, view, kind, query, offset, version]);
  const refresh = () => { setNotice(''); setBootVersion((value) => value + 1); setVersion((value) => value + 1); };
  const save = async (payload) => {
    if (modal.record) await api.update(modal.kind, modal.record.id, payload, modal.record.revision);
    else await api.create(modal.kind, payload);
    setModal(null); setNotice('Registro guardado directamente en BigQuery.');
    if (kind === 'catalogos') {
      try { setOptions((await api.options()).options); }
      catch (failure) { setNotice(`Registro guardado. No se pudieron actualizar las sugerencias: ${errorText(failure)}`); }
    }
    setVersion((value) => value + 1);
  };
  const remove = async () => {
    setBusy(true); setError('');
    try {
      await api.remove(kind, deleting.id, deleting.revision);
      setDeleting(null); setNotice('Registro eliminado de las consultas y del dashboard.');
      if (kind === 'catalogos') {
        try { setOptions((await api.options()).options); }
        catch (failure) { setNotice(`Registro eliminado. No se pudieron actualizar las sugerencias: ${errorText(failure)}`); }
      }
      if (list?.data.length === 1 && offset > 0) setOffset(Math.max(0, offset - PAGE_SIZE));
      setVersion((value) => value + 1);
    } catch (failure) { setError(errorText(failure)); }
    finally { setBusy(false); }
  };
  return <main className="sp-page weekly-report">
    <header className="sp-heading"><div><p className="sp-eyebrow">Servicios Profesionales</p>
      <h1><BarChart3 size={26} />Reporte Semanal Cloud</h1><p>Siete tablas de captura, sin Excel, borradores ni publicación de cortes.</p></div>
      <div className="sp-tabs" role="tablist" aria-label="Vista del reporte">
        <button role="tab" aria-selected={view === 'dashboard'} disabled={Boolean(modal || deleting)}
          onClick={() => setView('dashboard')}><LayoutDashboard size={17} />Dashboard</button>
        <button role="tab" aria-selected={view === 'gestion'} disabled={Boolean(modal || deleting)}
          onClick={() => setView('gestion')}><ListChecks size={17} />Gestión</button>
      </div>
    </header>
    <div className="sp-toolbar"><button disabled={loading || Boolean(modal || deleting)} onClick={refresh}><RefreshCw size={16} />Recargar datos</button></div>
    {notice && <p role="status" className="sp-feedback">{notice}</p>}
    {error && <p role="alert" className="sp-feedback error">{error} Recarga antes de reintentar si hubo un conflicto o tiempo de espera.</p>}
    {tables && view === 'dashboard' && <>
      <form className="sp-toolbar weekly-filters" onSubmit={(event) => { event.preventDefault(); setApplied({ ...range, ...filters }); }}>
        <label>Desde<input type="date" required value={range.start} max={range.end} onChange={(event) => setRange({ ...range, start: event.target.value })} /></label>
        <label>Hasta<input type="date" required value={range.end} min={range.start} onChange={(event) => setRange({ ...range, end: event.target.value })} /></label>
        <label>Cliente<input value={filters.cliente} maxLength={300} onChange={(event) => setFilters({ ...filters, cliente: event.target.value })} /></label>
        <label>Nube<input list="weekly-clouds" value={filters.nube} maxLength={300} onChange={(event) => setFilters({ ...filters, nube: event.target.value })} /></label>
        <datalist id="weekly-clouds">{(options.nube || []).map((value) => <option key={value} value={value} />)}</datalist>
        <label>Responsable / Preventa / Dueño<input value={filters.responsable} maxLength={300} onChange={(event) => setFilters({ ...filters, responsable: event.target.value })} /></label>
        <button className="sp-primary" disabled={loading}>Aplicar filtros</button>
      </form>
      <p className="weekly-scope">Mostrando registros del {dateLabel(applied.start)} al {dateLabel(applied.end)}.</p>
      <details className="weekly-filter-help"><summary>Cómo funcionan los filtros</summary>
        <p>Selecciona fechas, cliente, nube o responsable y pulsa «Aplicar filtros» para consultar sus avances. No modifica los registros guardados.</p>
        <p>Si un proyecto u oportunidad tiene capturas en distintas semanas, cada captura del periodo cuenta como un registro.</p>
        <p>Highlights/Riesgos se filtra por fecha, cliente y dueño, pero no por nube. Tickets del mes siempre muestra la última captura global del mes seleccionado hasta la fecha «Hasta».</p>
      </details>
    </>}
    {tables && view === 'gestion' && <>
      <div className="sp-tabs weekly-sheet-tabs" role="tablist" aria-label="Tablas del reporte">
        {Object.entries(tables).map(([key, table]) => <button key={key} role="tab" aria-selected={kind === key}
          disabled={Boolean(modal || deleting)} onClick={() => { setKind(key); setOffset(0); setSearch(''); setQuery(''); }}>{table.label}</button>)}
      </div>
      <section className="weekly-panel">
        <div className="weekly-management-heading"><h2>{tables[kind].label} {list && <small>{list.total} registros</small>}</h2>
          <label className="sp-search"><Search size={16} /><span className="weekly-sr-only">Buscar registros</span>
            <input aria-label="Buscar registros" placeholder="Buscar..." value={search} maxLength={300} onChange={(event) => setSearch(event.target.value)} /></label>
          <button className="sp-primary" disabled={loading} onClick={() => setModal({ kind, record: null })}><Plus size={16} />Nuevo registro</button>
        </div>
        {kind === 'catalogos' && <p>13 listas independientes, con las mismas columnas del Excel. Una fila puede tener solo una columna llena; eliminarla retira todas las opciones de esa fila.</p>}
        {kind === 'diccionario' && <p>Guía editable de campos; no cambia el esquema físico de las tablas.</p>}
        {!loading && list && <>
          <ReportTable table={tables[kind]} records={list.data} onEdit={(record) => setModal({ kind, record })} onDelete={setDeleting} />
          <div className="sp-toolbar weekly-pagination">
            <span>Página {Math.floor(offset / PAGE_SIZE) + 1} · {list.total} registros</span>
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Anterior</button>
            <button disabled={offset + PAGE_SIZE >= list.total} onClick={() => setOffset(offset + PAGE_SIZE)}>Siguiente</button>
          </div>
        </>}
      </section>
    </>}
    {loading && <p role="status" className="sp-loading">Cargando tablas…</p>}
    {!loading && view === 'dashboard' && data && <>
      {undated.some((entry) => entry.sin_fecha > 0) && <p role="status" className="sp-feedback error">
        Hay registros sin fecha fuera del dashboard (conteo global): {undated.filter((entry) => entry.sin_fecha > 0)
          .map((entry) => `${tables[entry.tabla].label}: ${entry.sin_fecha}`).join(', ')}. Corrígelos desde Gestión.
      </p>}
      <Dashboard data={data} tables={tables} />
    </>}
    {modal && <RecordForm table={tables[modal.kind]} record={modal.record} options={options}
      onClose={() => setModal(null)} onSave={save} />}
    {deleting && <WeeklyDialog title="Eliminar registro" busy={busy} onClose={() => setDeleting(null)}>
      <p>El registro dejará de aparecer en esta tabla y en el dashboard. Se conserva la baja y su autor en BigQuery para auditoría.</p>
      {kind === 'catalogos' && <p>Esto retira las opciones de las 13 columnas de esta fila; no borra los valores ya capturados en otras tablas.</p>}
      {error && <p role="alert" className="sp-feedback error">{error}</p>}
      <footer className="sp-actions sp-editor-footer"><button disabled={busy} onClick={() => setDeleting(null)}>Cancelar</button>
        <button className="sp-danger" disabled={busy} onClick={remove}>Confirmar eliminación</button></footer>
    </WeeklyDialog>}
  </main>;
}
